import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { parseExcelFile, downloadTemplate } from "@/lib/excel-parser";
import { validateUsers, formatDateForDB, type ValidatedUser } from "@/lib/user-import-validator";
import { Upload, Download, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ImportUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'preview' | 'processing' | 'complete';

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

export const ImportUsersDialog = ({ isOpen, onClose, onImportComplete }: ImportUsersDialogProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState<ImportStep>('upload');
  const [validatedUsers, setValidatedUsers] = useState<ValidatedUser[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an Excel (.xlsx, .xls) or CSV file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "File size must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    try {
      const parsed = await parseExcelFile(file);
      const validated = validateUsers(parsed);
      setValidatedUsers(validated);
      setStep('preview');
    } catch (error: any) {
      toast({
        title: "Error parsing file",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleImport = async () => {
    setIsProcessing(true);
    setStep('processing');

    const validUsers = validatedUsers.filter(u => u.isValid);
    const errors: string[] = [];
    let successCount = 0;

    for (const user of validUsers) {
      try {
        // Set defaults
        const startDate = user.start_date ? formatDateForDB(user.start_date) : new Date().toISOString().split('T')[0];
        const endDate = user.end_date ? formatDateForDB(user.end_date) : 
          new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
        const planCode = user.plan_code || 'ADMIN_COMP';
        const status = user.status?.toLowerCase() || 'active';

        // Create profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            name: user.name,
            phone: user.phone || null,
            whatsapp: user.whatsapp || null,
            status: status,
            auth_user_id: null // Will be linked when user signs up
          })
          .select()
          .single();

        if (profileError) {
          errors.push(`Row ${user.row_number}: ${profileError.message}`);
          continue;
        }

        // Create complimentary subscription
        const { error: subError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: profile.id,
            plan_code: planCode,
            source: 'complimentary',
            status: 'active',
            start_at: startDate,
            end_at: endDate
          });

        if (subError) {
          errors.push(`Row ${user.row_number}: Failed to create subscription - ${subError.message}`);
          // Rollback profile creation
          await supabase.from('profiles').delete().eq('id', profile.id);
          continue;
        }

        successCount++;
      } catch (error: any) {
        errors.push(`Row ${user.row_number}: ${error.message}`);
      }
    }

    setResult({
      success: successCount,
      failed: errors.length,
      errors
    });
    setIsProcessing(false);
    setStep('complete');

    if (successCount > 0) {
      onImportComplete();
    }
  };

  const handleClose = () => {
    setStep('upload');
    setValidatedUsers([]);
    setResult(null);
    onClose();
  };

  const validCount = validatedUsers.filter(u => u.isValid).length;
  const invalidCount = validatedUsers.length - validCount;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Users</DialogTitle>
          <DialogDescription>
            Upload an Excel or CSV file to bulk import users
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="max-w-xs mx-auto"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Supports .xlsx, .xls, and .csv files (max 5MB)
              </p>
            </div>

            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Required columns:</strong> name<br />
                <strong>Optional columns:</strong> phone, whatsapp, email, plan_code, start_date, end_date, status
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {validCount} valid users, {invalidCount} with errors
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('upload')}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={validCount === 0}
                >
                  Import {validCount} Users
                </Button>
              </div>
            </div>

            <div className="border rounded-lg max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Validation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validatedUsers.map((user, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{user.row_number}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.phone || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={user.isValid ? "default" : "destructive"}>
                          {user.isValid ? 'Valid' : 'Invalid'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.validationErrors.length > 0 && (
                          <div className="text-xs text-destructive">
                            {user.validationErrors.map((err, i) => (
                              <div key={i}>{err.message}</div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">Importing users...</p>
            <p className="text-sm text-muted-foreground">Please wait</p>
          </div>
        )}

        {step === 'complete' && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-semibold">Import Complete</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-2xl font-bold text-green-600">{result.success}</p>
                <p className="text-sm text-muted-foreground">Successfully imported</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                <p className="text-sm text-muted-foreground">Failed</p>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="border rounded-lg p-4 max-h-48 overflow-auto">
                <p className="font-medium mb-2">Errors:</p>
                {result.errors.map((error, idx) => (
                  <p key={idx} className="text-sm text-destructive">{error}</p>
                ))}
              </div>
            )}

            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
